import FutureAnalysis from "./components/FutureAnalysis";
import PastAnalysis from "./components/PastAnalysis";
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { UnknownAction } from "@reduxjs/toolkit";
import summarySlice from "./context/summarySlice";
import { SummaryStateProps } from "./context/types";
import classes from "./summary.module.scss";
import { Stack } from "@altimateai/lego";
import Opportunities from "./components/Opportunities";
import SummaryChatbot from "./components/SummaryChatbot";
import { isOwner } from "@/services/auth";
import {
  Button,
  Typography,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { useNavigate } from "react-router-dom";
import { useCostAttribution } from "@/pages/settings/components/userGroups/useCostAttribution";
import { RelativeComponentLoader } from "@/components/loader";
import { shouldRedirectToTeamSpends } from "@/helpers/tenant";
import { Shines2Icon } from "@/assets/icons";
import { useGlobalPlaygroundActions } from "@/modules/playground";
import dayjs from "dayjs";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useBreadcrumb } from "@/components/breadcrumbs";

interface ContextProps {
  state: SummaryStateProps;
  dispatch: React.Dispatch<UnknownAction>;
  selectedInstance: string;
}

export const SummaryContext = createContext<ContextProps>({
  state: summarySlice.getInitialState(),
  dispatch: () => null,
  selectedInstance: "",
});

const Summary = (): JSX.Element => {
  const [selectedInstance, setSelectedInstance] = useState<string>("");
  const [state, dispatch] = useReducer(summarySlice.reducer, summarySlice.getInitialState());
  const { openPlayground } = useGlobalPlaygroundActions();

  useBreadcrumb("Summary");

  const { datastores, enableCostReports, isDatabricksTenant } = useAppState();
  const { costAttribution, isLoading: costAttributionLoading } = useCostAttribution();
  const shouldRedirect = shouldRedirectToTeamSpends() && !isOwner();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      !costAttributionLoading &&
      costAttribution?.enabled &&
      costAttribution.mode === "query" &&
      shouldRedirect
    ) {
      navigate("/team-spends");
    }
  }, [costAttribution, costAttributionLoading, navigate, shouldRedirect]);

  const values = useMemo(
    () => ({
      state,
      dispatch,
      selectedInstance,
    }),
    [state, dispatch, selectedInstance]
  );

  const onTabSelect = (value: string) => {
    document.getElementById(value)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAnalyzeClick = () => {
    const startDate = dayjs().subtract(1, "month").toDate();
    const endDate = dayjs().toDate();
    openPlayground({
      chatbotProps: { initialMessage: "Analyze my cost" },
      context: {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        summary_analysis_type: "analyze_cost",
      },
    });
  };

  if (
    shouldRedirect &&
    (costAttributionLoading || (costAttribution?.enabled && costAttribution.mode === "query"))
  ) {
    return <RelativeComponentLoader label={"Loading..."} />;
  }

  return (
    <SummaryContext.Provider value={values}>
      <div className={classes.summary}>
        <Stack className={classes.stickyHeader}>
          <Typography variant="page-heading">Summary</Typography>
          <Stack direction="row" align="center" justify="between" gap={"sm"}>
            <Stack direction="row" align="center" gap={"sm"}>
              {!isDatabricksTenant && (
                <Select value={selectedInstance} onValueChange={setSelectedInstance}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {datastores?.map(store => (
                      <SelectItem key={store.instance_id} value={store.instance_id || ""}>
                        {store.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {[
                { label: "Current State", value: "current" },
                { label: "Future State", value: "future" },
                ...(isDatabricksTenant ? [] : [{ label: "Opportunities", value: "opportunities" }]),
              ].map(item => (
                <Button
                  variant="ghost"
                  className="al-font-normal al-text-sm al-text-gray-500"
                  key={item.value}
                  value={item.value}
                  onClick={() => onTabSelect(item.value)}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Stack
            direction="column"
            style={{
              flex: 1,
              maxWidth: state.showChatbot ? "calc(100% - 400px)" : "100%",
            }}
          >
            {!isDatabricksTenant && !enableCostReports && isOwner() && (
              <Stack className={classes.datapilotAnalysisCoverUp}>
                <div className={classes.text}>
                  DataPilot can analyze your usage for last 30 days and uncover key highlights
                </div>
                <Button className={classes.button} onClick={handleAnalyzeClick}>
                  <Shines2Icon className="!al-w-[24px] !al-h-[24px] [&_rect]:al-opacity-0" />{" "}
                  Analyze
                </Button>
              </Stack>
            )}
            <PastAnalysis />
            <FutureAnalysis />
            {!isDatabricksTenant && <Opportunities instanceId={selectedInstance} />}
          </Stack>
          <SummaryChatbot />
        </Stack>
      </div>
    </SummaryContext.Provider>
  );
};

export const useSummaryDispatch = (): React.Dispatch<UnknownAction> => {
  const { dispatch } = useContext(SummaryContext);
  return dispatch;
};

export const useSelectedInstance = (): string => {
  const { selectedInstance } = useContext(SummaryContext);
  return selectedInstance;
};

export default Summary;
